export interface PhoneFrontCameraFov {
  id: string;
  label: string;
  publishedFovDeg: number;
  publishedAxis: 'unspecified';
  conversionAssumption: 'treated-as-4:3-diagonal';
  horizontalFovDeg: number;
  sourceUrl: string;
}

/** Convert a published diagonal FOV to horizontal for the front camera's native 4:3 image. */
export function horizontalFovFromFourThreeDiagonal(diagonalFovDeg: number): number {
  if (!Number.isFinite(diagonalFovDeg) || diagonalFovDeg <= 0 || diagonalFovDeg >= 180) {
    throw new RangeError('Published field of view must be between 0° and 180°');
  }
  const diagonalRadians = (diagonalFovDeg * Math.PI) / 180;
  return (2 * Math.atan(Math.tan(diagonalRadians / 2) * (4 / 5)) * 180) / Math.PI;
}

function phone(id: string, label: string, publishedFovDeg: number, sourceUrl: string): PhoneFrontCameraFov {
  return {
    id,
    label,
    publishedFovDeg,
    publishedAxis: 'unspecified',
    conversionAssumption: 'treated-as-4:3-diagonal',
    horizontalFovDeg: horizontalFovFromFourThreeDiagonal(publishedFovDeg),
    sourceUrl,
  };
}

/**
 * These manufacturers publish front/selfie-camera FOV values in their hardware specifications.
 * The pages do not label the axis, so this prototype visibly treats each as diagonal
 * and converts it to 4:3 horizontal FOV. Camera apps may crop further; every result is approximate.
 */
export const PHONE_FRONT_CAMERAS: readonly PhoneFrontCameraFov[] = [
  // Source: https://support.google.com/pixelphone/answer/7158570?hl=en (Pixel 8 / Pixel 9: 95° ultrawide front-camera FOV)
  phone('pixel-8-9-front', 'Google Pixel 8 / 9 · front', 95, 'https://support.google.com/pixelphone/answer/7158570?hl=en'),
  // Source: https://support.google.com/pixelphone/answer/7158570?hl=en (Pixel 9 Pro / Pro XL: 103° ultrawide front-camera FOV)
  phone('pixel-9-pro-front', 'Google Pixel 9 Pro / Pro XL · front', 103, 'https://support.google.com/pixelphone/answer/7158570?hl=en'),
  // Source: https://news.samsung.com/global/enter-the-new-era-of-mobile-ai-with-samsung-galaxy-s24-series (S24 / S24+ / S24 Ultra: front camera FOV 80°)
  phone('galaxy-s24-front', 'Samsung Galaxy S24 series · front', 80, 'https://news.samsung.com/global/enter-the-new-era-of-mobile-ai-with-samsung-galaxy-s24-series'),
  // Source: https://www.sony.com/electronics/support/mobile-phones-tablets-mobile-phones/xperia-1-v-256gb/specifications (Xperia 1 V: front camera FOV 83°)
  phone('xperia-1-v-front', 'Sony Xperia 1 V · front', 83, 'https://www.sony.com/electronics/support/mobile-phones-tablets-mobile-phones/xperia-1-v-256gb/specifications'),
] as const;

export const DEFAULT_CUSTOM_HORIZONTAL_FOV_DEG = 80;

export function phoneHorizontalFov(id: string, customHorizontalFovDeg: number): number {
  const match = PHONE_FRONT_CAMERAS.find((entry) => entry.id === id);
  if (match) return match.horizontalFovDeg;
  if (!Number.isFinite(customHorizontalFovDeg) || customHorizontalFovDeg < 20 || customHorizontalFovDeg > 150) {
    throw new RangeError('Custom horizontal field of view must be between 20° and 150°');
  }
  return customHorizontalFovDeg;
}

export function phoneProfileMatchesFacingMode(id: string, facingMode: string | undefined): boolean {
  return !PHONE_FRONT_CAMERAS.some((entry) => entry.id === id) || facingMode === 'user';
}
